import { useEffect, useState } from "react";
import { useBlocker } from "react-router-dom";
import { Card, Progress } from "@mantine/core";
import { HiExclamationCircle, HiExclamationTriangle } from "react-icons/hi2";
import { CustomLoadingOverlay } from "../../../ui/CustomLoadingOverlay/CustomLoadingOverLay";
import CustomLeaveConfirmationModal from "../../../ui/CustomLeaveConfirmationModal/CustomLeaveConfirmationModal";
import { useStep3 } from "../hooks/useStep3";
import ValidationRecipientsTable from "../components/ValidationRecipientsTable";
import RecipientEditDrawer from "../components/RecipientEditDrawer";
import ManualEntrySuccessModal from "../../ManualEntry/components/ManualEntrySuccessModal";
import CustomButton from "../../../ui/CustomButton/CustomButton";
import { formatKES } from "../../../../utils/transactionutils";

function Step3ReviewAndApprove() {
  const {
    disbursementId,
    batchData,
    totalAmount,
    uiState,
    isApproving,
    successModalOpen,
    handleApprove,
    handlePostEditRefetch,
    handleNewUpload,
    handleViewHistory,
  } = useStep3();

  const [editingRow, setEditingRow] = useState(null);

  const hasValidationErrors = uiState === "validation-errors";
  const paymentChannel = batchData?.paymentChannel ?? "";

  // Once approved, the success modal's own buttons are the sanctioned exit —
  // the guard must not also block that navigation.
  const shouldGuardLeave = !successModalOpen;

  const blocker = useBlocker(shouldGuardLeave);

  useEffect(() => {
    if (!shouldGuardLeave) return;
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [shouldGuardLeave]);

  return (
    <div className="flex flex-col gap-6">
      <CustomLoadingOverlay
        fullScreen
        visible={isApproving}
        overlayProps={{ blur: 1.4, backgroundOpacity: 0.15 }}
        loaderProps={{ color: "primary", type: "bars" }}
      />

      <CustomLeaveConfirmationModal
        opened={blocker.state === "blocked"}
        message="This batch has been created but still needs to be submitted for approval. Leaving now will clear it from your flow — you won't be able to come back and approve it later."
        onStay={() => blocker.reset()}
        onLeave={() => blocker.proceed()}
      />
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900">
          Step 3: Review Transactions
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Final review of bulk payment details before batch processing.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card radius="md" shadow="sm" p="lg" className="bg-white">
          <div className="flex justify-between items-start mb-6">
            <div>
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">
                Total Batch Amount
              </p>
              <p className="text-2xl font-bold text-primary-600 mt-1">
                {formatKES(totalAmount)}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">
                Recipients
              </p>
              <p className="text-2xl font-bold text-gray-800 mt-1">
                {batchData?.recipientCount ?? "—"}
              </p>
            </div>
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-1.5">Batch Progress</p>
            <Progress value={100} color="primary" radius="xl" size="md" />
            <p className="text-xs text-gray-400 mt-1 text-right">Step 3 of 3</p>
          </div>
        </Card>

        <div className="rounded-xl bg-primary-50 border border-primary-100 p-5 flex flex-col justify-center">
          <div className="flex items-center gap-2 mb-2">
            <HiExclamationCircle
              size={20}
              className="text-primary-600 shrink-0"
            />
            <p className="font-semibold text-primary-700">Review Required</p>
          </div>
          <p className="text-sm text-primary-600 leading-relaxed">
            Please ensure all account details and amounts are correct. Errors
            highlighted in red must be resolved before submission.
          </p>
        </div>
      </div>

      {/* Validation error banner */}
      {hasValidationErrors && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
          <HiExclamationTriangle size={18} className="text-red-500 shrink-0" />
          <p className="text-sm text-red-700">
            Please resolve all failed transaction(s) before submitting for
            approval.
          </p>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <ValidationRecipientsTable
          disbursementId={disbursementId}
          paymentChannel={paymentChannel}
          onEdit={setEditingRow}
        />
      </div>

      <div className="flex justify-end pb-4">
        <div className="bg-white border border-gray-200 rounded-xl px-3 py-1.5">
          <CustomButton
            radius="md"
            color="primary"
            loading={isApproving}
            disabled={hasValidationErrors}
            onClick={handleApprove}
          >
            Submit for Approval
          </CustomButton>
        </div>
      </div>

      <RecipientEditDrawer
        opened={!!editingRow}
        recipientRow={editingRow}
        disbursementId={disbursementId}
        onClose={() => setEditingRow(null)}
        onSuccess={() => {
          setEditingRow(null);
          handlePostEditRefetch();
        }}
      />

      <ManualEntrySuccessModal
        opened={successModalOpen}
        batchId={batchData?.batchId}
        onAddAnother={handleNewUpload}
        onViewHistory={handleViewHistory}
      />
    </div>
  );
}

export default Step3ReviewAndApprove;
